import { Box, Tooltip } from "@mui/material";

import { formatRateValueForDisplay } from "../../../utils/rateFormatters";
import { slotKeyOf, type RenewalColumns } from "../../../utils/rateSets";

import { FONT_SIZE, AG_BORDER, gridRowSx, headerCellSx } from "./constants";

import type { Rate } from "../../../types";
import type { FC } from "react";

/**
 * Header cell for the read-only renewal / revised-renewal column. Labeled "Renewal" or
 * "Rev. Ren." (revised takes precedence). Sits as the right-hand column with a left
 * divider from the current rates. Self-nulls when the plan has no renewal.
 */
export const RenewalHeaderCell: FC<{ show: boolean; label: string }> = ({
  show,
  label,
}) =>
  show ? (
    <Box sx={{ ...headerCellSx, borderRight: "none", borderLeft: AG_BORDER }}>
      {label}
    </Box>
  ) : null;

/**
 * Full header row for a two-column (Label + Value) grid that gains a renewal column: an
 * empty label cell, a right-aligned "Current" cell, and the renewal header cell. Self-nulls
 * when the plan has no renewal.
 */
export const RenewalHeaderRow: FC<{ show: boolean; label: string }> = ({
  show,
  label,
}) =>
  show ? (
    <Box sx={gridRowSx}>
      <Box sx={{ ...headerCellSx }} />
      <Box sx={{ ...headerCellSx, justifyContent: "flex-end" }}>Current</Box>
      <RenewalHeaderCell show={show} label={label} />
    </Box>
  ) : null;

interface RenewalRateCellProps {
  /** slotKeyOf(rate) for the row this cell sits in. */
  slotKey: string;
  renewal: RenewalColumns;
  /** Row label for the aria-label (e.g. tier or age-band label). */
  rowLabel: string;
  isLastRow: boolean;
  /** The plan's coverage type, which formatRateValue also reads for precision. */
  coverageType?: string;
}

/**
 * Read-only renewal / revised-renewal value cell for one rate slot, with a tooltip
 * enumerating every populated set (current / renewal / revised) for that slot. Rendered
 * only when the plan has renewal data; carrier columns never mount it.
 */
export const RenewalRateCell: FC<RenewalRateCellProps> = ({
  slotKey,
  renewal,
  rowLabel,
  isLastRow,
  coverageType,
}) => (
  <Tooltip
    arrow
    title={
      <Box sx={{ fontSize: FONT_SIZE }}>
        <Box>
          Current:{" "}
          {formatRateValueForDisplay(
            renewal.currentBySlot[slotKey],
            renewal.unitBySlot[slotKey],
            coverageType,
          )}
        </Box>
        {renewal.renewalBySlot[slotKey] != null && (
          <Box>
            Renewal:{" "}
            {formatRateValueForDisplay(
              renewal.renewalBySlot[slotKey],
              renewal.unitBySlot[slotKey],
              coverageType,
            )}
          </Box>
        )}
        {renewal.revisedBySlot[slotKey] != null && (
          <Box>
            Revised Renewal:{" "}
            {formatRateValueForDisplay(
              renewal.revisedBySlot[slotKey],
              renewal.unitBySlot[slotKey],
              coverageType,
            )}
          </Box>
        )}
      </Box>
    }
  >
    <Box
      tabIndex={0}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        borderLeft: AG_BORDER,
        borderBottom: isLastRow ? "none" : AG_BORDER,
        px: 1,
        fontSize: FONT_SIZE,
        // Read-only, but match the editable cells' hover shade and let the analyst
        // select/copy the value (text cursor + selection).
        cursor: "text",
        userSelect: "text",
        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
      }}
      aria-label={`${rowLabel} ${renewal.columnLabel}`}
    >
      {formatRateValueForDisplay(
        renewal.columnBySlot[slotKey],
        renewal.unitBySlot[slotKey],
        coverageType,
      )}
    </Box>
  </Tooltip>
);

/**
 * Group-header cell spanning a rate table's renewal tracks (e.g. "Renewal" over the four
 * NT-EE / NT-SP / T-EE / T-SP renewal columns). Self-nulls when the plan has no renewal,
 * so a renderer places it unconditionally and its own cyclomatic complexity stays flat.
 */
export const RenewalGroupHeaderCell: FC<{
  show: boolean;
  label: string;
  span: number;
}> = ({ show, label, span }) =>
  show ? (
    <Box
      sx={{
        ...headerCellSx,
        gridColumn: `span ${span}`,
        justifyContent: "center",
        fontWeight: 600,
        borderRight: "none",
      }}
    >
      {label}
    </Box>
  ) : null;

/**
 * Per-slot sub-label header cells for the renewal tracks (one label per renewal column),
 * with the left divider on the first and no right border on the last. Self-nulls when the
 * plan has no renewal.
 */
export const RenewalSubHeaderCells: FC<{
  show: boolean;
  labels: string[];
}> = ({ show, labels }) =>
  show ? (
    <>
      {labels.map((label, idx) => (
        <Box
          key={`renewal-label-${label}`}
          sx={{
            ...headerCellSx,
            borderLeft: idx === 0 ? AG_BORDER : undefined,
            borderRight: idx === labels.length - 1 ? "none" : undefined,
            justifyContent: "flex-end",
          }}
        >
          {label}
        </Box>
      ))}
    </>
  ) : null;

/**
 * The renewal value cells for one grid row — one per current rate slot, in the same order
 * as the current columns. A slot with no rate renders an empty divider cell so the columns
 * stay aligned. Self-nulls when the plan has no renewal.
 */
export const RenewalRowCells: FC<{
  show: boolean;
  renewal: RenewalColumns;
  /** Prefixes each cell's aria row label (e.g. the age-band label). */
  rowLabelPrefix: string;
  isLastRow: boolean;
  cells: { rate: Rate | undefined; label: string }[];
  /** Slot-key function; must match the one `renewal` was built with (defaults to slotKeyOf). */
  keyOf?: (rate: Rate) => string;
}> = ({
  show,
  renewal,
  rowLabelPrefix,
  isLastRow,
  cells,
  keyOf = slotKeyOf,
}) =>
  show ? (
    <>
      {cells.map(({ rate, label }) =>
        rate ? (
          <RenewalRateCell
            key={`renewal-${label}`}
            slotKey={keyOf(rate)}
            renewal={renewal}
            rowLabel={[rowLabelPrefix, label].filter(Boolean).join(" ")}
            isLastRow={isLastRow}
          />
        ) : (
          <Box
            key={`renewal-empty-${label}`}
            sx={{
              borderLeft: AG_BORDER,
              borderBottom: isLastRow ? "none" : AG_BORDER,
            }}
          />
        ),
      )}
    </>
  ) : null;
